import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip,
} from "recharts";
import {
    CalendarCheck, Clock, CheckCircle2, XCircle, BookOpen, ArrowLeft, Flame,
} from "lucide-react";

const RANGE_PRESETS = [
    { id: "7", label: "Last 7 days", days: 6 },
    { id: "30", label: "Last 30 days", days: 29 },
    { id: "90", label: "Last 90 days", days: 89 },
];

function iso(d) {
    return d.toISOString().slice(0, 10);
}

function defaultRange(days) {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    return { start: iso(start), end: iso(end) };
}

function fmtHours(secs) {
    const h = Math.floor((secs || 0) / 3600);
    const m = Math.round(((secs || 0) % 3600) / 60);
    if (h <= 0) return `${m}m`;
    return `${h}h ${m}m`;
}

function fmtShortDate(d) {
    const dt = new Date(`${d}T00:00:00Z`);
    return `${dt.getUTCMonth() + 1}/${dt.getUTCDate()}`;
}

function fmtLongDate(d) {
    const dt = new Date(`${d}T00:00:00Z`);
    return dt.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
    });
}

export default function MyAttendance() {
    const [enrollments, setEnrollments] = useState([]);
    const [programId, setProgramId] = useState("");
    const [rangeId, setRangeId] = useState("30");
    const [range, setRange] = useState(defaultRange(29));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        api.get("/me/enrollments")
            .then((r) => {
                const list = r.data || [];
                setEnrollments(list);
                if (list[0]) setProgramId(list[0].program_id);
            })
            .catch(() => setEnrollments([]));
    }, []);

    useEffect(() => {
        if (!programId) return;
        setLoading(true);
        api.get("/me/attendance", {
            params: { program_id: programId, start: range.start, end: range.end },
        })
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [programId, range]);

    const chartData = useMemo(() => {
        if (!data) return [];
        return data.days.map((day) => {
            const cell = data.row.cells?.[day] || {};
            return {
                day: fmtShortDate(day),
                minutes: Math.round((cell.active_seconds || 0) / 60),
                status: cell.status || "absent",
            };
        });
    }, [data]);

    const totals = data?.row?.totals || { present: 0, absent: 0, active_seconds: 0 };
    const days = data?.days || [];
    const cells = data?.row?.cells || {};
    const hoursStudied = fmtHours(totals.active_seconds);

    const streak = useMemo(() => {
        if (!days.length) return 0;
        let s = 0;
        for (let i = days.length - 1; i >= 0; i -= 1) {
            const c = cells[days[i]];
            if (c?.status === "present") s += 1;
            else break;
        }
        return s;
    }, [days, cells]);

    const onRangeChange = (val) => {
        setRangeId(val);
        const preset = RANGE_PRESETS.find((p) => p.id === val);
        if (preset) setRange(defaultRange(preset.days));
    };

    return (
        <div
            className="max-w-6xl mx-auto px-6 py-12 pb-24"
            data-testid="my-attendance-page"
        >
            <Link
                to="/dashboard"
                className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"
            >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to dashboard
            </Link>

            <div className="flex items-end justify-between mt-2 mb-8 flex-wrap gap-3">
                <div>
                    <div className="inline-flex items-center gap-1.5 chip chip-primary">
                        <CalendarCheck className="h-3 w-3" />
                        Study time
                    </div>
                    <h1 className="font-display text-3xl md:text-4xl text-foreground mt-3 tracking-tight">
                        My Attendance
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">
                        Heritage tracks the time you spend on each program. 10+ minutes a day
                        in the learn view counts as a present day toward your attendance
                        record.
                    </p>
                </div>
            </div>

            {enrollments.length === 0 ? (
                <Card className="border-dashed border-border">
                    <CardContent className="p-12 text-center">
                        <div className="h-16 w-16 rounded-full bg-primary/8 text-primary flex items-center justify-center mx-auto mb-4">
                            <BookOpen className="h-7 w-7" />
                        </div>
                        <p className="font-display text-xl text-foreground">
                            No enrolments yet
                        </p>
                        <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                            Enrol in a program — your daily study time will be tracked here.
                        </p>
                        <Button asChild className="mt-5 bg-primary hover:bg-primary/90">
                            <Link to="/">Browse the catalog</Link>
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <>
                    {/* Filters */}
                    <Card className="border-border mb-6">
                        <CardContent className="p-5 flex flex-col sm:flex-row gap-4">
                            <div className="flex-1">
                                <label
                                    htmlFor="prog-select"
                                    className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1.5 block"
                                >
                                    Program
                                </label>
                                <Select value={programId} onValueChange={setProgramId}>
                                    <SelectTrigger
                                        id="prog-select"
                                        data-testid="my-attendance-program-trigger"
                                    >
                                        <SelectValue placeholder="Pick a program" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {enrollments.map((e) => (
                                            <SelectItem
                                                key={e.program_id}
                                                value={e.program_id}
                                            >
                                                {e.program?.title || e.program_id}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="sm:w-56">
                                <label
                                    htmlFor="range-select"
                                    className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1.5 block"
                                >
                                    Date range
                                </label>
                                <Select value={rangeId} onValueChange={onRangeChange}>
                                    <SelectTrigger
                                        id="range-select"
                                        data-testid="my-attendance-range-trigger"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {RANGE_PRESETS.map((p) => (
                                            <SelectItem key={p.id} value={p.id}>
                                                {p.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardContent>
                    </Card>

                    {/* KPI tiles */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        <Kpi
                            label="Present days"
                            value={totals.present}
                            Icon={CheckCircle2}
                            tone="primary"
                        />
                        <Kpi
                            label="Absent days"
                            value={totals.absent}
                            Icon={XCircle}
                            tone="muted"
                        />
                        <Kpi
                            label="Total studied"
                            value={hoursStudied}
                            Icon={Clock}
                            tone="accent"
                        />
                        <Kpi
                            label="Current streak"
                            value={streak}
                            suffix={streak === 1 ? "day" : "days"}
                            Icon={Flame}
                            tone="accent"
                        />
                    </div>

                    {/* Chart */}
                    <Card className="border-border mb-6">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-display text-lg text-foreground tracking-tight">
                                    Daily study minutes
                                </h3>
                                <Badge
                                    variant="outline"
                                    className="text-[10px] border-primary/30 text-primary"
                                >
                                    {RANGE_PRESETS.find((p) => p.id === rangeId)?.label}
                                </Badge>
                            </div>
                            <div className="h-64 -mx-2">
                                {loading ? (
                                    <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                                        Loading…
                                    </div>
                                ) : (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={chartData}>
                                            <CartesianGrid
                                                strokeDasharray="3 3"
                                                stroke="hsl(var(--border))"
                                            />
                                            <XAxis
                                                dataKey="day"
                                                tick={{
                                                    fontSize: 11,
                                                    fill: "hsl(var(--muted-foreground))",
                                                }}
                                                axisLine={false}
                                                tickLine={false}
                                            />
                                            <YAxis
                                                tick={{
                                                    fontSize: 11,
                                                    fill: "hsl(var(--muted-foreground))",
                                                }}
                                                axisLine={false}
                                                tickLine={false}
                                            />
                                            <Tooltip
                                                cursor={{ fill: "hsl(var(--muted) / 0.4)" }}
                                                contentStyle={{
                                                    background: "hsl(var(--card))",
                                                    border: "1px solid hsl(var(--border))",
                                                    borderRadius: 6,
                                                }}
                                                formatter={(v) => [`${v} min`, "Studied"]}
                                            />
                                            <Bar
                                                dataKey="minutes"
                                                radius={[4, 4, 0, 0]}
                                                fill="hsl(var(--primary))"
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Day-by-day list */}
                    <Card className="border-border">
                        <CardContent className="p-0">
                            <div className="px-6 py-4 border-b border-border">
                                <h3 className="font-display text-lg text-foreground tracking-tight">
                                    Day-by-day
                                </h3>
                            </div>
                            <div className="divide-y divide-border max-h-[420px] overflow-y-auto">
                                {[...days].reverse().map((day) => {
                                    const c = cells[day] || {};
                                    const mins = Math.round((c.active_seconds || 0) / 60);
                                    return (
                                        <div
                                            key={day}
                                            className="px-6 py-3 flex items-center gap-3 hover:bg-muted/30 transition-colors"
                                            data-testid={`my-attendance-day-${day}`}
                                        >
                                            <div className="text-sm text-muted-foreground w-32">
                                                {fmtLongDate(day)}
                                            </div>
                                            <div className="flex-1">
                                                {c.status === "present" ? (
                                                    <Badge
                                                        variant="outline"
                                                        className="border-primary/40 text-primary bg-primary/5 text-[10px]"
                                                    >
                                                        <CheckCircle2 className="h-3 w-3 mr-1" />
                                                        Present
                                                    </Badge>
                                                ) : c.status === "pre_enrolment" ? (
                                                    <Badge
                                                        variant="outline"
                                                        className="text-[10px] text-muted-foreground"
                                                    >
                                                        Pre-enrolment
                                                    </Badge>
                                                ) : (
                                                    <Badge
                                                        variant="outline"
                                                        className="text-[10px] text-muted-foreground"
                                                    >
                                                        <XCircle className="h-3 w-3 mr-1" />
                                                        Absent
                                                    </Badge>
                                                )}
                                            </div>
                                            <div className="text-sm font-medium tabular-nums text-foreground w-20 text-right">
                                                {mins > 0 ? `${mins} min` : "—"}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
}

function Kpi({ label, value, suffix, Icon, tone }) {
    const cls =
        tone === "primary"
            ? "text-primary"
            : tone === "accent"
              ? "text-accent-foreground"
              : "text-foreground";
    const iconCls =
        tone === "primary"
            ? "bg-primary/10 text-primary"
            : tone === "accent"
              ? "bg-accent/15 text-accent-foreground"
              : "bg-muted/40 text-muted-foreground";
    return (
        <Card className="surface-card border-border">
            <CardContent className="p-5">
                <div className="flex items-center justify-between">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                        {label}
                    </div>
                    <div className={`h-8 w-8 rounded-md flex items-center justify-center ${iconCls}`}>
                        <Icon className="h-4 w-4" />
                    </div>
                </div>
                <div className={`font-display text-3xl mt-2 leading-none ${cls}`}>
                    {value}
                    {suffix && (
                        <span className="text-base text-muted-foreground ml-1 font-sans">
                            {suffix}
                        </span>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
